import { useCallback, useRef } from 'react'
import type { ShiftManagementShiftInteractorDropActionEnum } from 'generated/resources/graphql'

import {
  getColumnCellCoords,
  getRowCellCoords,
} from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import { useClipboardShortcuts } from 'modules/shift_management/hooks/gridTable/useClipboardShortcuts'
import type {
  ClipboardHandlers,
  ClipboardPayloadEntry,
  ClipboardSelection,
  GridSelection,
  Row,
  TimeGridCellContext,
  TimeGridEntity,
  TimeGridEvent,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'

type ClipboardScopeKind = 'row' | 'column' | 'cells'

interface GridLayout<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  rows: Row<TEntity, TEvent>[]
  slots: TimeSlot[]
  rowByEntityId: Map<string, Row<TEntity, TEvent>>
  rowIndexByEntityId: Map<string, number>
}

interface GridSelectionInput {
  selection: GridSelection
  selectedFocusedCell: { entityId: string; slotIndex: number } | null
  fromCellKey: (key: string) => { entityId: string; slotIndex: number }
}

interface UseGridClipboardProps<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent> {
  layout: GridLayout<TEntity, TEvent>
  selection: GridSelectionInput
  clipboardHandlers: ClipboardHandlers<TEntity, TEvent> | undefined
  clearSelection: () => void
}

function useGridClipboard<TEntity extends TimeGridEntity, TEvent extends TimeGridEvent>({
  layout,
  selection,
  clipboardHandlers,
  clearSelection,
}: UseGridClipboardProps<TEntity, TEvent>): void {
  const { rows, slots, rowByEntityId, rowIndexByEntityId } = layout
  const { selection: selectionState, selectedFocusedCell, fromCellKey } = selection

  const copiedCellRef = useRef<{
    source: ClipboardSelection<TEntity, TEvent>
    payload: ClipboardPayloadEntry[]
    mode: ShiftManagementShiftInteractorDropActionEnum
    scopeKind: ClipboardScopeKind
  } | null>(null)

  const buildCellContext = useCallback(
    (cell: {
      entityId: string
      slotIndex: number
    }): TimeGridCellContext<TEntity, TEvent> | null => {
      const slot = slots[cell.slotIndex]
      if (!slot) return null

      const row = rowByEntityId.get(cell.entityId)
      if (!row) return null

      const cellEvents = row.layouts
        .filter(
          (layoutRow) =>
            layoutRow.span.startIndex <= cell.slotIndex && layoutRow.span.endIndex >= cell.slotIndex
        )
        .map((layoutRow) => layoutRow.event)

      return {
        entity: row.entity,
        slot,
        events: cellEvents,
      }
    },
    [rowByEntityId, slots]
  )

  const buildClipboardSelection = useCallback(
    (
      cells: Array<{ entityId: string; slotIndex: number }>,
      anchorCell: { entityId: string; slotIndex: number }
    ): ClipboardSelection<TEntity, TEvent> | null => {
      const anchorContext = buildCellContext(anchorCell)
      const anchorRowIndex = rowIndexByEntityId.get(anchorCell.entityId)
      if (!anchorContext || anchorRowIndex === undefined) return null

      const resolvedCells = cells
        .map((cell) => {
          const context = buildCellContext(cell)
          const rowIndex = rowIndexByEntityId.get(cell.entityId)
          if (!context || rowIndex === undefined) return null

          return {
            context,
            rowOffset: rowIndex - anchorRowIndex,
            columnOffset: cell.slotIndex - anchorCell.slotIndex,
          }
        })
        .filter((cell): cell is NonNullable<typeof cell> => cell !== null)
        .sort((left, right) => {
          if (left.rowOffset !== right.rowOffset) return left.rowOffset - right.rowOffset
          return left.columnOffset - right.columnOffset
        })

      if (resolvedCells.length === 0) return null

      return {
        anchor: anchorContext,
        cells: resolvedCells,
      }
    },
    [buildCellContext, rowIndexByEntityId]
  )

  const buildRowSelection = useCallback(
    (rowId: string): ClipboardSelection<TEntity, TEvent> | null => {
      const anchorCell = { entityId: rowId, slotIndex: 0 }
      const cells = getRowCellCoords(rowId, slots.length)
      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, slots.length]
  )

  const buildColumnSelection = useCallback(
    (columnIndex: number): ClipboardSelection<TEntity, TEvent> | null => {
      const firstRow = rows[0]
      if (!firstRow) return null
      const anchorCell = { entityId: firstRow.entity.id, slotIndex: columnIndex }
      const cells = getColumnCellCoords(columnIndex, rows)
      return buildClipboardSelection(cells, anchorCell)
    },
    [buildClipboardSelection, rows]
  )

  const handleCopyOrCut = useCallback(
    (mode: ShiftManagementShiftInteractorDropActionEnum) => {
      if (!clipboardHandlers) return

      const hasRowSelection = selectionState.kind === 'row'
      const hasColumnSelection = selectionState.kind === 'column'
      const hasCellsSelection = selectionState.kind === 'cells' && selectionState.keys.size > 0
      if (!hasRowSelection && !hasColumnSelection && !hasCellsSelection) return

      let sourceSelection: ClipboardSelection<TEntity, TEvent> | null = null
      let scopeKind: ClipboardScopeKind = 'cells'

      if (hasRowSelection) {
        sourceSelection = buildRowSelection(selectionState.rowId)
        scopeKind = 'row'
      } else if (hasColumnSelection) {
        sourceSelection = buildColumnSelection(selectionState.columnIndex)
        scopeKind = 'column'
      } else if (hasCellsSelection) {
        const sourceCells = Array.from(selectionState.keys).map(fromCellKey)
        sourceSelection = buildClipboardSelection(sourceCells, selectionState.anchor)
      }

      if (!sourceSelection) return

      const payload = clipboardHandlers.getCopyPayload(sourceSelection)
      if (payload === null) return

      const nextCopiedCell = {
        source: sourceSelection,
        payload,
        mode,
        scopeKind,
      }
      copiedCellRef.current = nextCopiedCell
    },
    [
      buildClipboardSelection,
      buildColumnSelection,
      buildRowSelection,
      clipboardHandlers,
      fromCellKey,
      selectionState,
    ]
  )

  const handlePaste = useCallback(async () => {
    const copied = copiedCellRef.current
    if (!copied || !clipboardHandlers) return

    let targetSelection: ClipboardSelection<TEntity, TEvent> | null = null

    if (copied.scopeKind === 'row') {
      if (selectionState.kind !== 'row') return
      targetSelection = buildRowSelection(selectionState.rowId)
    } else if (copied.scopeKind === 'column') {
      if (selectionState.kind !== 'column') return
      targetSelection = buildColumnSelection(selectionState.columnIndex)
    } else {
      if (!selectedFocusedCell) return
      const targetAnchorRowIndex = rowIndexByEntityId.get(selectedFocusedCell.entityId)
      if (targetAnchorRowIndex === undefined) return

      const targetCells = copied.payload
        .map((entry) => {
          const targetRow = rows[targetAnchorRowIndex + entry.rowOffset]
          if (!targetRow) return null

          const targetSlotIndex = selectedFocusedCell.slotIndex + entry.columnOffset
          if (targetSlotIndex < 0 || targetSlotIndex >= slots.length) return null

          return {
            entityId: targetRow.entity.id,
            slotIndex: targetSlotIndex,
          }
        })
        .filter((cell): cell is NonNullable<typeof cell> => cell !== null)

      targetSelection = buildClipboardSelection(targetCells, selectedFocusedCell)
    }

    if (!targetSelection) return

    const canPaste = clipboardHandlers.canPaste?.({
      source: copied.source,
      target: targetSelection,
      payload: copied.payload,
    })

    if (canPaste === false) return

    await clipboardHandlers.onPaste({
      source: copied.source,
      target: targetSelection,
      payload: copied.payload,
      mode: copied.mode,
    })
  }, [
    buildClipboardSelection,
    buildColumnSelection,
    buildRowSelection,
    clipboardHandlers,
    rowIndexByEntityId,
    rows,
    selectionState,
    selectedFocusedCell,
    slots.length,
  ])

  const handleEscape = useCallback(() => {
    clearSelection()
  }, [clearSelection])

  const handleCopy = useCallback(() => {
    handleCopyOrCut('copy')
  }, [handleCopyOrCut])

  const handleCut = useCallback(() => {
    handleCopyOrCut('move')
  }, [handleCopyOrCut])

  useClipboardShortcuts({
    onCopy: clipboardHandlers ? handleCopy : undefined,
    onCut: clipboardHandlers ? handleCut : undefined,
    onPaste: clipboardHandlers ? handlePaste : undefined,
    onEscape: handleEscape,
  })
}

export { useGridClipboard }
